import * as THREE from 'three/webgpu';
import type {SketchRelationDefinition} from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import {
    isAbstractSketchEdgeObject,
    type ISketchObject,
    type SketchCloneContext,
} from '../../mesh/sketch';
import {readSketchEntityReferenceTag} from '../../reference/sketch-entity-reference/model/SketchEntityReferenceIds';

/**
 * 转换实体引用对象的复制约束策略。
 *
 * 设计说明：
 * 1. 转换实体引用对象通过稳定引用标记识别，不按直线、圆、圆弧等具体类型分支；
 * 2. 命中对象后排除实体本体及其关联控制点上的全部几何约束；
 * 3. 策略只返回约束准入函数，不负责对象克隆，Ctrl+C/V 与复制实体命令可以复用同一规则；
 * 4. 使用约束实例集合而不是关系类型集合，避免误伤同一批选择中的普通草图实体。
 */
export class SketchReferencedEntityCopyConstraintPolicy {
    /**
     * 为当前复制源构建约束复制准入函数。
     *
     * @param sourceEntities 经过选择归一后的复制源实体。
     * @returns 仅拒绝转换实体引用对象所属约束的准入函数。
     */
    public createClonePredicate(
        sourceEntities: readonly THREE.Object3D[],
    ): NonNullable<SketchCloneContext['shouldCloneConstraint']> {
        const excludedConstraints = this.collectExcludedConstraints(sourceEntities);

        // 克隆服务会对单目标和多目标关系复用同一准入函数，实例集合可保证两条路径判断一致。
        return (constraint) => !excludedConstraints.has(constraint);
    }

    /**
     * 收集转换实体引用对象复制时必须断开的约束实例。
     *
     * @param sourceEntities 经过选择归一后的复制源实体。
     * @returns 不允许进入副本的约束实例集合。
     */
    private collectExcludedConstraints(
        sourceEntities: readonly THREE.Object3D[],
    ): Set<SketchRelationDefinition<ISketchObject>> {
        const excludedConstraints = new Set<SketchRelationDefinition<ISketchObject>>();

        sourceEntities.forEach((entity) => {
            if (!readSketchEntityReferenceTag(entity) || !isAbstractSketchEdgeObject(entity)) {
                return;
            }

            // 转换实体本体承载引用驱动关系及可能追加的普通关系，两类关系都不进入副本。
            this.collectObjectConstraints(entity, excludedConstraints);

            /**
             * 直线端点、圆心、圆弧端点等控制点属于同一转换实体定义域。
             * 若只清理边对象约束，固定点或端点关系仍会让副本表现为受源关系约束。
             */
            entity.getRelatedSketchPointsSnapshot().forEach((point) => {
                this.collectObjectConstraints(point, excludedConstraints);
            });
        });

        return excludedConstraints;
    }

    /**
     * 把单个草图对象容器中的约束加入排除集合。
     *
     * @param object 约束宿主对象。
     * @param target 排除约束集合。
     */
    private collectObjectConstraints(
        object: ISketchObject,
        target: Set<SketchRelationDefinition<ISketchObject>>,
    ): void {
        object.constraints.getRelations().forEach((constraint) => {
            target.add(constraint as SketchRelationDefinition<ISketchObject>);
        });
    }
}
